import { spanFromRegExpMatch } from '../spans';
import type { ExtractorOutput } from './types';

/**
 * Platform sort keys (same as listing-params Sort):
 * price | -price | date | -date | bedrooms | -bedrooms | bathrooms | -bathrooms
 */
interface SortRule {
  pattern: RegExp;
  sort: string;
  ruleId: string;
  confidence: number;
}

/**
 * More specific phrases first. Ascending price = cheapest; descending date = newest.
 */
const SORT_RULES: ReadonlyArray<SortRule> = [
  {
    pattern:
      /\b(?:cheapest(?:\s+first)?|lowest\s+price(?:\s+first)?|price\s*(?:low\s+to\s+high|asc(?:ending)?)|sort(?:ed)?\s+by\s+price\s*(?:asc(?:ending)?|low\s+to\s+high)?)\b/gi,
    sort: 'price',
    ruleId: 'sort.price-asc',
    confidence: 0.95,
  },
  {
    pattern:
      /\b(?:most\s+expensive(?:\s+first)?|highest\s+price(?:\s+first)?|price\s*(?:high\s+to\s+low|desc(?:ending)?)|sort(?:ed)?\s+by\s+price\s*(?:desc(?:ending)?|high\s+to\s+low))\b/gi,
    sort: '-price',
    ruleId: 'sort.price-desc',
    confidence: 0.95,
  },
  {
    pattern:
      /\b(?:newest(?:\s+first)?|most\s+recent(?:\s+first)?|recently\s+listed\s+first|date\s*(?:new\s+to\s+old|desc(?:ending)?)|sort(?:ed)?\s+by\s+date\s*(?:desc(?:ending)?|newest)?)\b/gi,
    sort: '-date',
    ruleId: 'sort.date-desc',
    confidence: 0.9,
  },
  {
    pattern:
      /\b(?:oldest(?:\s+first)?|date\s*(?:old\s+to\s+new|asc(?:ending)?)|sort(?:ed)?\s+by\s+date\s*(?:asc(?:ending)?|oldest)?)\b/gi,
    sort: 'date',
    ruleId: 'sort.date-asc',
    confidence: 0.9,
  },
  {
    pattern:
      /\b(?:most\s+bedrooms?(?:\s+first)?|bedrooms?\s*(?:high\s+to\s+low|desc(?:ending)?)|sort(?:ed)?\s+by\s+bedrooms?\s*(?:desc(?:ending)?|high\s+to\s+low))\b/gi,
    sort: '-bedrooms',
    ruleId: 'sort.bedrooms-desc',
    confidence: 0.85,
  },
  {
    pattern:
      /\b(?:fewest\s+bedrooms?(?:\s+first)?|bedrooms?\s*(?:low\s+to\s+high|asc(?:ending)?)|sort(?:ed)?\s+by\s+bedrooms?\s*(?:asc(?:ending)?|low\s+to\s+high)?)\b/gi,
    sort: 'bedrooms',
    ruleId: 'sort.bedrooms-asc',
    confidence: 0.85,
  },
  {
    pattern:
      /\b(?:most\s+bathrooms?(?:\s+first)?|bathrooms?\s*(?:high\s+to\s+low|desc(?:ending)?)|sort(?:ed)?\s+by\s+bathrooms?\s*(?:desc(?:ending)?|high\s+to\s+low))\b/gi,
    sort: '-bathrooms',
    ruleId: 'sort.bathrooms-desc',
    confidence: 0.85,
  },
  {
    pattern:
      /\b(?:fewest\s+bathrooms?(?:\s+first)?|bathrooms?\s*(?:low\s+to\s+high|asc(?:ending)?)|sort(?:ed)?\s+by\s+bathrooms?\s*(?:asc(?:ending)?|low\s+to\s+high)?)\b/gi,
    sort: 'bathrooms',
    ruleId: 'sort.bathrooms-asc',
    confidence: 0.85,
  },
];

export function extractSort(query: string): ExtractorOutput {
  for (const rule of SORT_RULES) {
    rule.pattern.lastIndex = 0;
    const match = rule.pattern.exec(query);
    if (!match) continue;

    return {
      partial: { sort: rule.sort },
      spans: [
        spanFromRegExpMatch(query, match, 'sort', rule.sort, {
          confidence: rule.confidence,
          ruleId: rule.ruleId,
        }),
      ],
    };
  }

  return { partial: {}, spans: [] };
}
